import { Button, Image, Modal } from "antd";
import { downloadThroughAnchorLink, isImage } from "../../utils/helpers";
import { DownloadOutlined, ImageOutlined } from "@mui/icons-material";
import { BASE_MEDIA_API_URL } from "../../config";
import { useState } from "react";
import { ReactComponent as FilePdfIcon } from "../../assets/svg/pdf-icon.svg";

const actionColor = "#1b75bb";

const buttonStyle = {
  background: actionColor,
  borderColor: actionColor,
  color: "#fff",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const downloadButtonStyle = {
  background: "#fff",
  borderColor: actionColor,
  color: actionColor,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const isPdf = (fileName: string) => /\.pdf$/i.test(fileName || "");
const isOfficeDoc = (fileName: string) =>
  /\.(docx?|xlsx?|pptx?)$/i.test(fileName || "");

// Office files can't render in the browser, so they go through Microsoft's viewer
const documentPreviewUrl = (uri: string, fileName: string) =>
  isOfficeDoc(fileName)
    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(uri)}`
    : uri;

// Save as "<caption>.<ext>" (e.g. "tin certificate.docx") rather than the
// stored UUID name, falling back to the original file name
const downloadName = (item, fileName: string) => {
  const label = item?.name || item?.caption?.replace(/_/g, " ");
  if (!label) return fileName;
  const ext = fileName?.split("?")[0].split(".").pop();
  return ext && !label.toLowerCase().endsWith(`.${ext.toLowerCase()}`)
    ? `${label}.${ext}`
    : label;
};

// The media server redirects http → https, and browsers refuse to follow a
// redirect on a cross-origin request, so request https up front
const toHttps = (url: string) =>
  /^http:\/\/(?!localhost|127\.0\.0\.1)/i.test(url)
    ? url.replace(/^http:/i, "https:")
    : url;

export const FileActionView = ({ uri, item, fileName }) => {
  const [visible, setVisible] = useState(false);
  const [docVisible, setDocVisible] = useState(false);
  const scaleStep = 0;

  const isDocument = isPdf(fileName) || isOfficeDoc(fileName);
  const canPreview = isImage(fileName) || isDocument;
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      await downloadThroughAnchorLink(uri, downloadName(item, fileName));
    } catch {
      // Media hosted on another origin can reject the authenticated request
      // (CORS), so let the browser fetch the file directly instead.
      window.open(uri, "_blank", "noopener,noreferrer");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <span>
        <Button
          type="primary"
          icon={
            isImage(fileName) ? (
              <ImageOutlined style={{ fontSize: 18 }} />
            ) : (
              <FilePdfIcon
                style={{ width: 16, height: 16 }}
                fill="currentColor"
              />
            )
          }
          style={buttonStyle}
          onClick={() =>
            isImage(fileName)
              ? setVisible(true)
              : isDocument
                ? setDocVisible(true)
                : download()
          }
        >
          {canPreview ? "View" : "Download"}
        </Button>
        <Modal
          open={docVisible}
          onCancel={() => setDocVisible(false)}
          title={item?.caption?.replace(/_/g, " ") || fileName}
          width={1000}
          centered
          destroyOnClose
          footer={
            <Button
              type="primary"
              icon={<DownloadOutlined style={{ fontSize: 18 }} />}
              style={buttonStyle}
              loading={downloading}
              onClick={download}
            >
              Download
            </Button>
          }
        >
          <iframe
            src={documentPreviewUrl(uri, fileName)}
            title={fileName}
            style={{
              width: "100%",
              height: "70vh",
              border: "none",
            }}
          />
        </Modal>
        {visible && (
          <Image
            style={{ display: "none" }}
            src={uri}
            preview={{
              visible,
              scaleStep,
              src: uri,
              onVisibleChange: (value) => {
                setVisible(value);
              },
            }}
          />
        )}
      </span>
      {canPreview && (
        <span style={{ margin: 5 }}>
          <Button
            icon={<DownloadOutlined style={{ fontSize: 18 }} />}
            style={downloadButtonStyle}
            loading={downloading}
            onClick={download}
            title="Download"
          />
        </span>
      )}
    </>
  );
};

// export const renderSingleFile = (item, isLocal = false) => {
//   const uri = isLocal
//     ? window.URL.createObjectURL(item?.file)
//     : `${BASE_MEDIA_API_URL}${item?.file}`;

//   const fileName = isLocal ? item?.file?.name : item.file;

//   return <FileActionView uri={uri} item={item} fileName={fileName} />;
// };

export const renderSingleFile = (item, isLocal = false) => {
  let uri = "";
  let fileName = "";

  if (isLocal) {
    uri = window.URL.createObjectURL(item?.file);
    fileName = item?.file?.name;
  } else {
    const isAbsoluteUrl = /^https?:\/\//i.test(item?.file);
    uri = isAbsoluteUrl
      ? toHttps(item.file)
      : `${BASE_MEDIA_API_URL}${item.file}`;
    fileName = isAbsoluteUrl
      ? item?.file?.split("/")?.pop() || "document"
      : item.file;
  }

  return <FileActionView uri={uri} item={item} fileName={fileName} />;
};

/* DOCUMENT EDITOR */
